import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { candidateLabel, plural } from '../utils/focus';

// Пустой «Сегодня» — не тупик, а момент выбора. Три состояния по очереди:
// 1) стопка карточек из того, что уже есть (планы, важные и старые идеи);
// 2) карточки кончились — быстрый ввод с подсказками из истории;
// 3) и там пусто — честный «свободный день» с превью завтра.
export default function EmptyDayPanel({
  candidates, doneCount, suggestions, tomorrow, onTake, onSkip, onQuickAdd, onDelete, onSortIdeas,
}) {
  const [text, setText] = useState('');
  const current = candidates[0];
  const allDone = doneCount > 0;

  const submit = (value) => {
    const v = (value ?? text).trim();
    if (!v) return;
    onQuickAdd(v);
    setText('');
  };

  // День закрыт: похвала уже в шапке («День закрыт · Вы молодец»), список сделанного — ниже. Здесь ничего:
  // ни «возьмёте ещё?», ни задач на завтра (Иван, 20.10.2026: «не нужна нам эта гонка»; тексты не дублируем)
  if (allDone) return null;

  if (current) {
    return (
      <div className="empty-day">
        <h3 className="empty-title">
          Что возьмём на сегодня?
        </h3>
        <p className="empty-sub">
          На сегодня пусто — выберите из того, что уже есть.
        </p>
        <div className="empty-stack">
          {candidates.length > 1 && <div className="empty-card-shadow" aria-hidden="true" />}
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.article
              key={current.task.id}
              className="empty-card"
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: -40, transition: { duration: 0.16 } }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            >
              <span className={`empty-source is-${current.source}`}>{candidateLabel(current)}</span>
              <h4 className="empty-card-title">{current.task.description}</h4>
              <div className="focus-actions">
                <button type="button" className="focus-btn focus-btn-primary" onClick={() => onTake(current.task)}>
                  Беру на сегодня
                </button>
                <button type="button" className="focus-btn focus-btn-secondary" onClick={() => onSkip(current.task)}>
                  Не сегодня
                </button>
              </div>
              <span className="empty-count">{candidates.length > 1 ? `ещё ${candidates.length - 1} на выбор` : 'последняя на выбор'}</span>
              {onDelete && (
                <button type="button" className="empty-delete" onClick={() => onDelete(current.task)}>
                  Неактуально — удалить
                </button>
              )}
            </motion.article>
          </AnimatePresence>
        </div>
        {onSortIdeas && (
          <button type="button" className="focus-link empty-sort-link" onClick={onSortIdeas}>
            Разобрать все идеи по важности →
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="empty-day">
      <h3 className="empty-title">Что хотите сделать сегодня?</h3>
      <p className="empty-sub">
        Запишите одно дело — с ним день уже не пустой. Можно и голосом боту.
      </p>
      <form className="empty-add" onSubmit={e => { e.preventDefault(); submit(); }}>
        <input
          id="empty-day-input"
          className="sheet-input"
          placeholder="Например: разобрать почту"
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <button type="submit" className="focus-btn focus-btn-primary" disabled={!text.trim()}>Добавить</button>
      </form>
      {suggestions.length > 0 && (
        <div className="empty-chips" aria-label="Вы часто делаете">
          {suggestions.map(s => (
            <button key={s} type="button" className="empty-chip" onClick={() => submit(s)}>＋ {s}</button>
          ))}
        </div>
      )}
      <div className="empty-tomorrow">
        <div className="focus-label">Завтра</div>
        {tomorrow.length ? (
          <ul>
            {tomorrow.slice(0, 3).map(t => <li key={t.id}>{t.description}</li>)}
            {tomorrow.length > 3 && <li className="empty-more">и ещё {tomorrow.length - 3}</li>}
          </ul>
        ) : (
          <p className="empty-sub">Пока ничего. Свободный день — тоже план.</p>
        )}
      </div>
    </div>
  );
}
